// CompanyCreation wizard — step 2: employee range (single) + business industry
// (single, with a "show more" reveal).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { employeeRanges, industries, INDUSTRY_PREVIEW_COUNT } from '@/mocks/onboarding';
import OptionCard from './OptionCard';
import type { CompanyCreationData } from './company-creation-types';
import type { FormErrors } from './validation';

interface StepProps {
  data: CompanyCreationData;
  onChange: (patch: Partial<CompanyCreationData>) => void;
  errors: FormErrors;
}

const CompanyCreationStepDetails = ({ data, onChange, errors }: StepProps) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const visibleIndustries = showAll ? industries : industries.slice(0, INDUSTRY_PREVIEW_COUNT);

  return (
    <div className='company-creation__fields'>
      <div className='company-creation__field'>
        <span className='company-creation__label'>
          {t('onb_employee_range')} <span className='company-creation__required'>*</span>
        </span>
        <div className='company-creation__ranges'>
          {employeeRanges.map((range) => (
            <button
              key={range.id}
              type='button'
              className={[
                'company-creation__range',
                data.employeeRange === range.id && 'company-creation__range--selected',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={data.employeeRange === range.id}
              onClick={() => onChange({ employeeRange: range.id })}
            >
              {t(range.labelKey)}
            </button>
          ))}
        </div>
        {errors.employeeRange && (
          <span className='company-creation__error'>{t(errors.employeeRange)}</span>
        )}
      </div>

      <div className='company-creation__field'>
        <span className='company-creation__label'>
          {t('onb_industry_question')} <span className='company-creation__required'>*</span>
        </span>
        <div className='company-creation__grid'>
          {visibleIndustries.map((industry) => (
            <OptionCard
              key={industry.id}
              icon={industry.icon}
              label={t(industry.labelKey)}
              selected={data.industry === industry.id}
              onClick={() => onChange({ industry: industry.id })}
            />
          ))}
        </div>
        {industries.length > INDUSTRY_PREVIEW_COUNT && !showAll && (
          <button
            type='button'
            className='company-creation__show-more'
            onClick={() => setShowAll(true)}
          >
            {t('onb_show_more_industries')}
          </button>
        )}
        {errors.industry && <span className='company-creation__error'>{t(errors.industry)}</span>}
      </div>
    </div>
  );
};

export default CompanyCreationStepDetails;
